import { useEffect, useRef, type ReactNode } from 'react'
import { MutationCache, QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider, persistQueryClientRestore } from '@tanstack/react-query-persist-client'
import { persistableClient, shouldPersistQuery } from '@/lib/persist-with-data'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { kvStorage } from '@floorfile/offline'
import { I18nProvider, localeMeta } from '@floorfile/i18n'
import { AuthProvider, useAuth } from '@/lib/auth'
import { useCompanyConfig } from '@/lib/company-config'
import { loadCatalogues } from './locale-catalogues'
import { EventFeedProvider } from '@/lib/event-feed'
import { ModulesProvider } from '@/lib/modules'
import { CompanyConfigProvider } from '@/lib/company-config'
import { OfflineBridge, offlineRuntime } from '@/lib/offline-runtime'
import { ErrorToasts, reportMutationError } from '@/lib/error-toasts'
import { AskProvider } from '@/components/AskProvider'
import { RegisterLayoutProvider } from '@floorfile/ui'
import { useRegisterWidthsBinding } from '@/lib/register-layout'
import { invalidateDerivedViews } from '@/lib/derived-views'
import { resolveDensity, resolveTheme, useAppShell } from './store'
import { isUnreachableError } from '@/lib/api-response'

/** A 4xx envelope: the request itself is wrong, and retrying it cannot help. */
function isClientError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

const queryClient: QueryClient = new QueryClient({
  // The safety net for every mutation (audit RUN-9). 85 call sites fire
  // `void mutation.mutateAsync(...)`, which attaches no rejection handler, and
  // there was no central `mutationCache` — so a failed save was an unhandled
  // rejection with no user feedback at all. Per-mutation `onError` is still
  // preferable where a page can say something specific; this guarantees the
  // user is never left believing a failed action succeeded.
  mutationCache: new MutationCache({
    // FF-QA-154 / FF-QA-052: a mutation whose screen already names the failure
    // opts out with `meta: { silentError: true }`; an error that carries
    // `handledInline` (an upload refusal the dialog shows) does the same.
    onError: (error, _variables, _context, mutation) => {
      if (mutation.meta?.['silentError'] === true) return
      if ((error as { handledInline?: unknown } | null)?.handledInline === true) return
      reportMutationError(error)
    },
    /**
     * FF-AUDIT-011 — derived reads refetch after ANY successful write.
     *
     * Entity-360 hubs, problem lineage, report runs and dashboards are
     * summaries computed ACROSS modules; each feature's mutation invalidates
     * its own family (`['parts']`, `['governance']`…) and could not know that
     * a stock transfer changes the part's hub card, or a hold release changes
     * the legal-hold report. Doing it here, once, is the "shared invalidation
     * audit" the finding asked for. Only mounted queries refetch; the rest are
     * merely marked stale, so the cost is one request per visible summary.
     *
     * FF-QA-046: a mutation that changes none of them (a checklist tick, an
     * analysis save) says so with `meta: { derivedViews: false }` and is
     * skipped — see `lib/derived-views.ts`.
     */
    onSuccess: (_data, _variables, _context, mutation): void => invalidateDerivedViews(queryClient, mutation.meta),
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      /**
       * One retry for a transport or server fault; NONE for a client error
       * (FF-AUDIT-094). A 404/403 answers the same on the second try, and
       * react-query pauses a pending retry while the window is not focused —
       * so a stale link to a deleted check sat on `Loading…` for as long as
       * the tab was in the background, instead of saying not found within
       * one round trip.
       */
      // FF-AUDIT-20260914-210459-096: an UNREACHABLE answer is not retried.
      // A retry in a dead zone is PAUSED by react-query, and a paused query
      // is neither loading nor errored — so a list with nothing cached
      // rendered as "no entries" and a detail page as blank. Settling to
      // `error` at once lets every screen say "can't reach the server" (or
      // keep its cached data), and the reconnect hook refetches later.
      retry: (failureCount, error) => !isUnreachableError(error) && failureCount < 1 && !isClientError(error),
      /**
       * FF-AUDIT-20260914-210459-096: attempt the request even when the
       * browser says it is offline. The service worker answers a dead-zone
       * call itself, so the query reaches a definite UNREACHABLE error (which
       * the screens word as such) instead of pausing on "Loading…" forever;
       * the app's own reachability monitor, not `navigator.onLine`, decides
       * when to refetch (see OfflineBridge).
       */
      networkMode: 'offlineFirst',
      refetchOnWindowFocus: false,
      // Reads must resolve from cache in a dead zone (D24 §3): persisted
      // data renders immediately; refetch reconciles when reachable.
      gcTime: 7 * 24 * 60 * 60 * 1000,
    },
  },
})

// Dev-only: a handle for inspecting query state from the browser pane while
// reproducing dead-zone behaviour (FF-AUDIT-20260914-210459-096). Absent in
// production builds.
if (import.meta.env.DEV) {
  ;(window as unknown as { __floorfileQueryClient?: QueryClient }).__floorfileQueryClient = queryClient
}

/**
 * Query families that must NEVER persist to disk: HR is declared never-cache
 * (D24 §9 — PII), and the /admin plane has its own boundary.
 *
 * The two dashboard roots are online-only by decision
 * (REPORTS_IMPLEMENTATION_PLAN step 10): a domain dashboard is a live aggregate
 * whose CONTENT is authorization-dependent — the server filters widgets by the
 * caller's permissions and facility scope on every request. A persisted copy
 * rehydrates with its ORIGINAL `dataUpdatedAt`, so after a role or facility
 * change it would render figures the user may no longer see and would not even
 * look stale enough to refetch. They have their own roots (rather than living
 * under `reports`) precisely so this exclusion does not also change the
 * existing report runner's behaviour.
 */
// `register-layouts` (F27) keeps its own user-scoped localStorage copy, which
// knows which local edits the server has not acknowledged; a second, older
// copy restored from here would only race it.
// `company-logo` (FF-QA-006): its data is a blob: object URL, dead after a reload.
const NEVER_PERSIST = new Set(['hr', 'admin', 'report-dashboards', 'report-dashboard-views', 'register-layouts', 'company-logo'])

/**
 * Persisted query cache (D24 §3) — with two guards the offline reload needs
 * (FF-AUDIT-088).
 *
 * 1. `PersistQueryClientProvider` restores exactly once, at mount — and at
 *    mount the encrypted KV is still LOCKED (no codec), so that read is a
 *    cache miss. The restore is run AGAIN once the PIN unlock installs the
 *    codec. Hydration only fills queries the client has no newer answer for,
 *    so after a normal online boot it is a no-op.
 * 2. The persister writes on every cache change, and between mount and the
 *    unlock the cache holds nothing but failed fetches. That write used to
 *    replace the encrypted snapshot — the form, the signatures, the lineage
 *    the device had already opened — with an empty one, seconds before the
 *    unlock that would have read it. Writes are therefore gated: off until
 *    the session is usable (authenticated, and unlocked when a vault exists)
 *    and the post-unlock restore has finished. `kvStorage` holds the second
 *    line — a locked writer never overwrites an `enc:` entry.
 */
let persistWritesEnabled = false
const baseStorage = kvStorage(offlineRuntime.db)
const persister = createAsyncStoragePersister({
  storage: {
    getItem: baseStorage.getItem,
    removeItem: baseStorage.removeItem,
    setItem: (key, value) => (persistWritesEnabled ? baseStorage.setItem(key, value) : Promise.resolve()),
  },
  key: 'floorfile-query-cache',
  throttleTime: 2_000,
  // FF-AUDIT-20260914-210459-097: an errored query that still holds data is
  // written as a stale success (see persist-with-data.ts) so the SECOND
  // offline reload finds the record the first one did.
  serialize: (client) => JSON.stringify(persistableClient(client)),
})
const PERSIST_MAX_AGE = 7 * 24 * 60 * 60 * 1000

function PersistGate({ children }: { children: ReactNode }) {
  const { status, offlineEncryption } = useAuth()
  const restoredAfterUnlock = useRef(false)
  useEffect(() => {
    if (status !== 'authenticated' || (offlineEncryption.enrolled && !offlineEncryption.unlocked)) {
      persistWritesEnabled = false
      return
    }
    if (offlineEncryption.unlocked && !restoredAfterUnlock.current) {
      restoredAfterUnlock.current = true
      persistWritesEnabled = false
      void persistQueryClientRestore({ queryClient, persister, maxAge: PERSIST_MAX_AGE })
        .catch(() => undefined)
        .finally(() => {
          persistWritesEnabled = true
        })
      return
    }
    persistWritesEnabled = true
  }, [status, offlineEncryption.enrolled, offlineEncryption.unlocked])
  return <>{children}</>
}

function ThemeApplier({ children }: { children: ReactNode }) {
  const theme = useAppShell((s) => s.theme)
  const setTheme = useAppShell((s) => s.setTheme)
  const { authFetch, status } = useAuth()
  useEffect(() => {
    if (status !== 'authenticated') return
    let active = true
    void authFetch('/api/v1/settings/profile')
      .then(async (response) => {
        if (!response.ok || !active) return
        const body = (await response.json()) as { data?: { theme?: 'LIGHT' | 'DARK' | 'SYSTEM' } }
        if (body.data?.theme && active) setTheme(body.data.theme.toLowerCase() as 'light' | 'dark' | 'system')
      })
      // Theme is a preference, not a blocker (audit RUN-20): an unreachable API
      // at mount must not produce an unhandled rejection. The stored theme
      // already applied, so silently keeping it is the correct degradation.
      .catch(() => undefined)
    return () => { active = false }
  }, [authFetch, setTheme, status])
  useEffect(() => {
    const root = document.documentElement
    const apply = () => {
      root.classList.toggle('dark', resolveTheme(theme) === 'dark')
    }
    apply()
    if (theme === 'system') {
      const media = window.matchMedia('(prefers-color-scheme: dark)')
      media.addEventListener('change', apply)
      return () => media.removeEventListener('change', apply)
    }
    return undefined
  }, [theme])
  // Density rides on <html> the same way the theme does (Fiix audit §8.2).
  const density = useAppShell((s) => s.density)
  useEffect(() => {
    const root = document.documentElement
    const apply = () => {
      root.dataset['density'] = resolveDensity(density)
    }
    apply()
    if (density === 'auto') {
      const media = window.matchMedia('(pointer: fine)')
      media.addEventListener('change', apply)
      return () => media.removeEventListener('change', apply)
    }
    return undefined
  }, [density])
  return <>{children}</>
}

/**
 * Feeds the i18n runtime from the already-correct server-side resolution
 * (`allowUserLocaleOverride ? user_preferences.locale : default_locale`,
 * surfaced as `effectiveLocale`) and the company time zone.
 *
 * Mounted INSIDE `CompanyConfigProvider` because it consumes that context, and
 * above everything that renders copy. `timeZone` is threaded here rather than
 * read separately at each formatting site — pairing locale with the COMPANY
 * zone, not the browser's, is the M-03 property `useFormat()` exists to make
 * unforgettable.
 */
function I18nBridge({ children }: { children: ReactNode }) {
  const { effectiveLocale, timezone } = useCompanyConfig()
  const meta = localeMeta(effectiveLocale)
  useEffect(() => {
    // `lang` drives screen-reader pronunciation and hyphenation; `dir` is the
    // D-L4 hygiene hook — we ship no RTL language, but a future one should be a
    // data change in `LOCALES`, not a sweep through the shell.
    document.documentElement.lang = meta.code
    document.documentElement.dir = meta.direction
  }, [meta.code, meta.direction])
  return (
    <I18nProvider
      locale={meta.code}
      timeZone={timezone}
      direction={meta.direction}
      loadCatalogues={loadCatalogues}
      // Shown only while the first screen waits for its words (≤ 3 s) — so it
      // carries no words itself.
      fallback={<div className="min-h-screen bg-background" aria-busy="true" />}
    >
      {children}
    </I18nProvider>
  )
}

export function Providers({ children }: { children: ReactNode }) {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSIST_MAX_AGE,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => shouldPersistQuery(query.state, String(query.queryKey[0]), NEVER_PERSIST),
        },
      }}
    >
      <AuthProvider>
        <PersistGate>
        <OfflineBridge>
          <EventFeedProvider>
            <CompanyConfigProvider>
              <I18nBridge>
                <ModulesProvider>
                  <ThemeApplier>
                    {/* The product’s confirm/prompt. Inside I18nBridge (its
                        buttons are catalogued) and around {children}, so any
                        feature can ask without mounting a dialog of its own. */}
                    <AskProvider>
                      {/* F27: every DataTable's column widths, saved per user. */}
                      <RegisterLayoutProvider useWidths={useRegisterWidthsBinding}>
                        {children}
                      </RegisterLayoutProvider>
                      <ErrorToasts />
                    </AskProvider>
                  </ThemeApplier>
                </ModulesProvider>
              </I18nBridge>
            </CompanyConfigProvider>
          </EventFeedProvider>
        </OfflineBridge>
        </PersistGate>
      </AuthProvider>
    </PersistQueryClientProvider>
  )
}
